/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { useTranslation } from '@scuf/localization';
import React, { memo, useContext } from 'react';
import descriptors from '../../../locales/en-US/descriptors';
import UtilityDetailSummaryComponent from '../../shared/DetailSummaryComponent/UtilityDetailSummaryComponent';
import UtilityTrendChart from '../../shared/TrendChart/UtilityTrendChart';
import { convertDataForUtilityTrendChart } from '../../shared/Utils/Utils';
import '../../../scss/ViewDetailsTheme.scss';
import {
  IAny,
  IUtilityBillElectData
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { getUtilityTotalAggregationValues } from '../../shared/Utils/unitHandling/EmaasSiteKpiDataScaling';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from '../../shared/Utils/unitHandling/unitHandling';
import { IUtilityBillAggregation } from '../UtilityBillViewDetailsComponent/IUtilityBillViewDetails';
import { ApiContext } from '../../../context/ApiContext';
import { ConversionType } from '../../shared/Utils/unitHandling/UnitModel';
import { PreferredUnit, UtilityType } from '../../shared/Utils/Constants';

interface IElectricityPerformanceDetailComponentProps {
  data: IUtilityBillElectData;
  UtilityBillType?: string /* once utilityType fixed from backend side then will remove this*/;
  isPrevPeriodForAllMonths?: boolean;
  isPrevPeriodForAllMonthsConsumption?: boolean;
}

const scaleFactor: IAny = {
  therm: 0.1,
  'kilowatt hour': 0.003412,
  'kilowatt hours': 0.003412,
  kilowatthour: 0.003412,
  kilowatt_hour: 0.003412,
  kilowatt_hours: 0.003412,
  scf: 0.001028,
  'standard cubic feet': 0.001028,
  standardcubicfeet: 0.001028,
  'standard cubic foot': 0.001028,
  standardcubicfoot: 0.001028,
  standard_cubic_feet: 0.001028,
  standard_cubic_foot: 0.001028,
  'million btu': 1,
  millionbtu: 1,
  mmbtu: 1,
  'cubic meter': 0.03661,
  cubic_meter: 0.03661,
  'cubic meters': 0.03661,
  cubic_meters: 0.03661,
  'standard cubic meter': 0.0332005312,
  mega_joules: 0.000947817,
  'thousand cubic feet': 1.038,
  'centum cubic feet': 0.1037
};
const GasPerformanceDetailComponent = (
  props: IElectricityPerformanceDetailComponentProps
) => {
  const d = descriptors.energyManagement;
  const [t, lng] = useTranslation();
  const {
    data,
    UtilityBillType,
    isPrevPeriodForAllMonths,
    isPrevPeriodForAllMonthsConsumption
  } = props;
  const apiContext = useContext(ApiContext);
  const { userPreferedUnit } = apiContext;

  const {
    utilityMeasurementUnit,
    utilityCostCurrency,
    totalAggregations,
    monthwiseAggregations
    // utilityType /* once utilityType fixed from backend side then will uncomment this*/
  } = data;
  const utilityType = UtilityBillType!;
  const {
    totalActualConsumption,
    previousPeriodConsumption,
    totalConsumptionSavings,
    totalConsumptionSavingsPercentage,
    totalActualCost,
    previousPeriodCost,
    totalCostSavings,
    totalCostSavingsPercentage
  } = totalAggregations;

  let fromUnit: string;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  // fromUnit = userPreferedUnit ? userPreferedUnit : 'kilowatt hour';

  fromUnit =
    utilityType.toLowerCase() === 'gas'
      ? PreferredUnit.Gas
      : utilityType.toLowerCase() === 'electricity'
      ? (PreferredUnit.Electricity as any)
      : utilityMeasurementUnit;

  // UtilityBillType === UtilityType.Gas && userPreferedUnit === 'megajoule';

  let isElectricUtilityOrGas =
    utilityType === UtilityType.Electricity ||
    utilityType.toLowerCase() === UtilityType.Electric ||
    utilityType === UtilityType.Gas;

  // let isElectricUtilityOrGas =
  //   utilityType === UtilityType.Electricity ||
  //   utilityType.toLowerCase() === UtilityType.Electric;

  const valuesArray = getUtilityTotalAggregationValues([totalAggregations]);
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    utilityType,
    utilityMeasurementUnit,
    valuesArray,
    utilityMeasurementUnit
  );

  const aggregationSummaryData = {
    actualConsumtion: totalActualConsumption,
    actualUnit: getMaxConvertedValueFromArray(
      utilityType,
      utilityMeasurementUnit,
      [totalActualConsumption],
      utilityMeasurementUnit
    ).Unit,
    baselineConsumption: previousPeriodConsumption,
    baselineUnit: getMaxConvertedValueFromArray(
      utilityType,
      utilityMeasurementUnit,
      [previousPeriodConsumption],
      utilityMeasurementUnit
    ).Unit,
    totalConsumptionSavings: !isPrevPeriodForAllMonthsConsumption
      ? null
      : totalConsumptionSavings,
    changeUnit: getMaxConvertedValueFromArray(
      utilityType,
      utilityMeasurementUnit,
      [totalConsumptionSavings],
      utilityMeasurementUnit
    ).Unit,
    totalConsumptionSavingsPercentage,
    actualCost: totalActualCost,
    baselineCost: previousPeriodCost,
    costSavings: !isPrevPeriodForAllMonths ? null : totalCostSavings,
    costSavingsPercentage: totalCostSavingsPercentage
  };

  const chartData = convertDataForUtilityTrendChart(
    UtilityBillType === UtilityType.Gas && userPreferedUnit === 'megajoule'
      ? monthwiseAggregations.map((mnth: IAny) => {
          return {
            ...mnth,
            totalActualConsumption:
              mnth.totalActualConsumption * scaleFactor[utilityMeasurementUnit],
            previousPeriodConsumption:
              mnth.previousPeriodConsumption *
              scaleFactor[utilityMeasurementUnit]
          };
        })
      : monthwiseAggregations,
    UtilityBillType === UtilityType.Gas && userPreferedUnit === 'megajoule'
      ? 'million btu'
      : utilityMeasurementUnit,
    maxValueUnitConversion.Unit,
    utilityType,
    lng
  );

  const legendItems = [
    {
      text: `${t(d.PREVIOUSPERIOD)}`,
      color: '#606061',
      className: 'emaas-legend-item-bar-baseline',
      type: 'Bar'
    },
    {
      text: `${t(d.ACTUAL)}`,
      color: '#1272B2',
      className: 'emaas-legend-item-bar-actual',
      type: 'Bar'
    },
    {
      text: `${t(d.PREVIOUSCOST)}`,
      color: '#AB7318',
      className: 'emaas-legend-item-line-baseline',
      type: 'Line'
    },
    {
      text: `${t(d.ACTUALCOST)}`,
      color: '#F5A623',
      className: 'emaas-legend-item-line-actual',
      type: 'Line'
    }
  ];
  return (
    <>
      <UtilityDetailSummaryComponent
        aggregationData={aggregationSummaryData as IAny}
        costCurrency={utilityCostCurrency!}
        measurementUnit={maxValueUnitConversion.Unit}
        originalUnit={utilityMeasurementUnit}
        forUtility={true}
        utilityType={utilityType}
        isPrevPeriodForAllMonths={isPrevPeriodForAllMonths}
        isPrevPeriodForAllMonthsConsumption={
          isPrevPeriodForAllMonthsConsumption
        }
      />
      <UtilityTrendChart
        measurementUnit={maxValueUnitConversion.Unit}
        costCurrency={utilityCostCurrency!}
        chartData={chartData}
        legendItems={legendItems}
      />
    </>
  );
};

export default memo(GasPerformanceDetailComponent);
